import { useEffect, useRef } from "react";

/**
 * Edge swipe para fechar overlays (Offcanvas/Modal).
 *
 * @param {Object} opts
 * @param {boolean} opts.enabled - Só ativa quando tiver algo aberto.
 * @param {Function} opts.onClose - Função chamada ao detectar swipe.
 * @param {"left"|"right"|"both"} [opts.side="both"] - Qual borda ativa o gesto.
 * @param {number} [opts.edge=24] - Px a partir da borda para considerar início do gesto.
 * @param {number} [opts.minDist=60] - Distância mínima do swipe (px).
 * @param {number} [opts.maxY=70] - Tolerância vertical (px).
 * @param {boolean} [opts.preventBrowserBack=true] - Evita gesto nativo de back do iOS.
 */
export function useEdgeSwipeClose({
  enabled,
  onClose,
  side = "both",
  edge = 24,
  minDist = 60,
  maxY = 70,
  preventBrowserBack = true,
} = {}) {
  const startedOnEdgeRef = useRef(false);
  const edgeSideRef = useRef(null); // "left" | "right" | null
  const startXRef = useRef(null);
  const startYRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    if (typeof onClose !== "function") return;

    const onTouchStart = (e) => {
      const t = e.touches?.[0];
      if (!t) return;

      const startX = t.clientX;
      const startY = t.clientY;
      const vw = window.innerWidth;

      startXRef.current = startX;
      startYRef.current = startY;

      let edgeSide = null;

      const isLeft = startX <= edge;
      const isRight = startX >= vw - edge;

      if (isLeft) edgeSide = "left";
      if (isRight) edgeSide = "right";

      // Respeita 'side'
      if (
        (side === "left" && edgeSide !== "left") ||
        (side === "right" && edgeSide !== "right") ||
        (side === "both" && !edgeSide)
      ) {
        startedOnEdgeRef.current = false;
        edgeSideRef.current = null;
        return;
      }

      startedOnEdgeRef.current = true;
      edgeSideRef.current = edgeSide;
    };

    const onTouchMove = (e) => {
      // Se começou na borda, evita o iOS interpretar como back navigation
      if (!preventBrowserBack) return;
      if (!startedOnEdgeRef.current) return;
      e.preventDefault();
    };

    const onTouchEnd = (e) => {
      if (!startedOnEdgeRef.current) return;

      const t = e.changedTouches?.[0];
      if (!t) return;

      const startX = startXRef.current;
      const startY = startYRef.current;
      const edgeSide = edgeSideRef.current;

      if (startX == null || startY == null || !edgeSide) return;

      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      const isHorizontal = Math.abs(dy) <= maxY;

      const leftSwipeClose = edgeSide === "left" && dx >= minDist && isHorizontal;
      const rightSwipeClose = edgeSide === "right" && dx <= -minDist && isHorizontal;

      if (leftSwipeClose || rightSwipeClose) {
        onClose();
      }

      startedOnEdgeRef.current = false;
      edgeSideRef.current = null;
      startXRef.current = null;
      startYRef.current = null;
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd, { passive: true });

    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, [enabled, onClose, side, edge, minDist, maxY, preventBrowserBack]);
}
